class InAppBrowserBanner extends HTMLElement {
  static detectionCacheKey = 'blossholm:in-app-browser-detection';
  static dismissCacheKey = 'blossholm:in-app-browser-banner-dismissed';
  static cacheVersion = 1;

  static inAppPatterns = [
    { pattern: /Instagram/i, app: 'instagram' },
    { pattern: /FB_IAB/i, also: /MESSENGER/i, app: 'messenger' },
    { pattern: /FBAN|FBAV/i, app: 'facebook' },
    { pattern: /BytedanceWebview|TTWebView/i, app: 'tiktok' },
    { pattern: /LinkedInApp/i, app: 'linkedin' },
  ];

  _abortController = null;
  _hideTimeout = null;

  connectedCallback() {
    if (this.hasAttribute('initialized')) {
      return;
    }

    this.setAttribute('initialized', 'true');

    this.openButton = this.querySelector('[data-in-app-browser-open]');
    this.dismissButton = this.querySelector('[data-in-app-browser-dismiss]');
    this.instructionElement = this.querySelector('[data-in-app-browser-instruction]');

    this.iosInstruction = this.dataset.iosInstruction || '';
    this.copiedInstruction = this.dataset.copiedInstruction || '';
    this.manualInstruction = this.dataset.manualInstruction || '';
    this.userAgent = window.navigator?.userAgent || '';

    if (this.isDesignMode()) {
      this.hidden = true;
      return;
    }

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.openButton?.addEventListener('click', () => this.handleOpenButtonClick(), { signal });
    this.dismissButton?.addEventListener('click', () => this.hide(), { signal });

    if (this.isDismissed()) {
      return;
    }

    const detection = this.getDetection();

    if (!detection.isInApp) {
      return;
    }

    this.detectedApp = detection.app;
    this.dataset.detectedApp = detection.app;
    this.show();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;

    if (this._hideTimeout) {
      window.clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
  }

  isDesignMode() {
    return this.dataset.designMode === 'true' || window.Shopify?.designMode === true;
  }

  isDismissed() {
    return this.constructor.readSessionValue(this.constructor.dismissCacheKey) === '1';
  }

  getDetection() {
    const { detectionCacheKey, cacheVersion } = this.constructor;
    const cached = this.constructor.readSessionValue(detectionCacheKey);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);

        if (parsed.version === cacheVersion && parsed.ua === this.userAgent && parsed.result) {
          return parsed.result;
        }
      } catch (error) {
        return this.cacheDetection();
      }
    }

    return this.cacheDetection();
  }

  cacheDetection() {
    const result = this.detectInAppBrowser();
    const { detectionCacheKey, cacheVersion } = this.constructor;

    this.constructor.writeSessionValue(
      detectionCacheKey,
      JSON.stringify({ version: cacheVersion, ua: this.userAgent, result })
    );

    return result;
  }

  detectInAppBrowser() {
    for (const { pattern, also, app } of this.constructor.inAppPatterns) {
      if (pattern.test(this.userAgent) && (!also || also.test(this.userAgent))) {
        return { isInApp: true, app };
      }
    }

    return { isInApp: false, app: '' };
  }

  async handleOpenButtonClick() {
    if (/Android/i.test(this.userAgent)) {
      window.location.href = this.buildAndroidIntentUrl(window.location.href);
      this._hideTimeout = window.setTimeout(() => this.showInstruction(this.manualInstruction), 900);
      return;
    }

    const didCopy = await this.copyCurrentUrl();
    this.showInstruction(didCopy ? this.copiedInstruction : this.iosInstruction);
  }

  buildAndroidIntentUrl(href) {
    const url = new URL(href);
    const scheme = url.protocol.replace(':', '');

    if (scheme !== 'http' && scheme !== 'https') {
      return href;
    }

    const fallbackUrl = encodeURIComponent(url.href);

    return [
      `intent://${url.host}${url.pathname}${url.search}#Intent`,
      `scheme=${scheme}`,
      'package=com.android.chrome',
      `S.browser_fallback_url=${fallbackUrl}`,
      'end',
    ].join(';');
  }

  async copyCurrentUrl() {
    if (!window.navigator.clipboard || !window.isSecureContext) {
      return false;
    }

    try {
      await window.navigator.clipboard.writeText(window.location.href);
      return true;
    } catch (error) {
      return false;
    }
  }

  showInstruction(message) {
    if (!this.instructionElement || !message) {
      return;
    }

    this.instructionElement.textContent = message;
    this.instructionElement.hidden = false;
  }

  show() {
    this.hidden = false;
    window.requestAnimationFrame(() => this.classList.add('is-visible'));
  }

  hide() {
    this.classList.remove('is-visible');
    this.constructor.writeSessionValue(this.constructor.dismissCacheKey, '1');

    this._hideTimeout = window.setTimeout(() => {
      this.hidden = true;
      this._hideTimeout = null;
    }, 220);
  }

  static readSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  static writeSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }
}

if (!window.customElements.get('in-app-browser-banner')) {
  window.customElements.define('in-app-browser-banner', InAppBrowserBanner);
}
